import { useEffect } from "react";
import classes from "./PaymentMethods.module.scss";
import { GooglePayButton } from "./GooglePayButton";
import { PayPalButton } from "./PayPalButton";
import { ApplePayButton } from "./ApplePayButton";
import { CreditCardButton } from "./CreditCardButton";
import type { PaymentMethodsProps } from "./PaymentMethodsProps";
import { useDonationFormStore } from "@/stores";
import { useBraintree } from "@/hooks/useBraintree";
import { usePaymentMethodsErrorsStore } from "./usePaymentMethodsErrorsStore";
import { Alert } from "@/components/ui/Alert";
import { usePaymentMethodsErrorsProcessor } from "./usePaymentMethodsErrorsProcessor";
import { useErrorProcessing } from "@/hooks/useErrorProcessing";

export function PaymentMethods(props: PaymentMethodsProps) {
  const { onPaymentSuccess, config } = props;

  const {
    "credit-card": creditCard,
    paypal: payPal,
    "google-pay": googlePay,
    "apple-pay": applePay,
  } = config;

  const { donationAmount, setPaymentMethod } = useDonationFormStore();

  const { errors, resetErrors } = usePaymentMethodsErrorsStore();

  const { braintreeClient } = useBraintree();

  const errorProcessor = usePaymentMethodsErrorsProcessor();
  const { registerProcessor, unregisterProcessor } = useErrorProcessing();

  useEffect(() => {
    registerProcessor(errorProcessor);

    return () => {
      unregisterProcessor(errorProcessor);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePaymentMethodSelect = async (paymentMethod: string) => {
    setPaymentMethod(paymentMethod);
    resetErrors();
    onPaymentSuccess?.(paymentMethod);
  };

  return (
    <div>
      {errors.payment && <Alert variant="error">{errors.payment}</Alert>}

      <div className={classes.grid}>
        {creditCard.enabled && (
          <CreditCardButton
            label="Credit/debit card"
            onClick={() => {
              handlePaymentMethodSelect(creditCard.value);
            }}
          />
        )}

        {payPal.enabled && (
          <PayPalButton
            braintreeClient={braintreeClient}
            amount={donationAmount ?? 0}
            onSuccess={() => {
              handlePaymentMethodSelect(payPal.value);
            }}
          />
        )}

        {googlePay.enabled && (
          <GooglePayButton
            braintreeClient={braintreeClient}
            amount={donationAmount ?? 0}
            onSuccess={() => {
              handlePaymentMethodSelect(googlePay.value);
            }}
          />
        )}

        {applePay.enabled && (
          <ApplePayButton
            braintreeClient={braintreeClient}
            amount={donationAmount ?? 0}
            onSuccess={() => {
              handlePaymentMethodSelect(applePay.value);
            }}
          />
        )}
      </div>
    </div>
  );
}
